/**
 * 畿内とその周辺の国データ
 *
 * 担当: 近江(1) / 伊勢(3) / 若狭(26) / 丹波(27)
 *
 * 石高ランクと防御は docs/01-ゲームデザイン.md §2.2 の盤上バランス値。
 * historicalKokudaka は慶長3年(1598)の国別石高（万石）を主に用い、
 * 盤上の値が調整済みであることを各国の gameNote に明記している。
 *
 * 執筆基準は _reference.ts（尾張・甲斐）と docs/05-学習設計.md §4 に従う。
 */

import type { Province } from '../types';

/**
 * 近江。寺社領（僧兵）の見本。延暦寺・園城寺の勢力を地形に反映させている。
 * 令制では東山道だが、盤上では京に隣接する畿内圏として扱う。
 */
export const OMI: Province = {
  id: 1,
  kind: 'province',
  name: '近江',
  reading: 'おうみ',
  region: 'kinai',
  kokudaka: 3,
  defense: 3,
  terrain: 'temple',
  historicalKokudaka: 77.5,
  yields: [{ res: 'zeni', n: 1 }],
  note: {
    summary:
      '琵琶湖を抱え、京へ通じる街道が集まる国。湖上水運と市が栄え、村の自治や最も古い楽市の記録が残る。',
    detail: {
      1: 'いまの滋賀県にあった国です。まんなかに大きな琵琶湖があり、荷物は船ではこばれました。京都へ行く道が何本も通る大事な場所で、市がひらかれてにぎわいました。のちに織田信長が安土城をたてた国でもあります。',
      2: '現在の滋賀県にあたる国。面積の六分の一ほどを琵琶湖が占め、湖上の水運と、京へ向かう街道が交わる交通の要衝だった。大津・坂本・堅田といった湖岸の町が物資の集散地として栄えている。守護は南に六角氏、北に京極氏があり、京極氏の家臣から浅井氏が台頭した。1549年に六角定頼が観音寺城下の石寺に楽市を認めた文書が残り、これは現在確認できる楽市令のうち最も古い。1570年の姉川の戦い、1573年の浅井氏の滅亡を経て、1576年に織田信長が安土城を築いた。',
      3: '現在の滋賀県。面積の約六分の一を琵琶湖が占め、東山道・北陸道・東海道が結節する交通の要衝であったため、石高以上の政治的比重を持った。慶長3年(1598)の石高は約77.5万石で全国でも上位にあたる。守護は南近江の六角氏と北近江の京極氏に分かれ、京極氏の被官であった浅井氏が16世紀に自立した。1549年に六角定頼が観音寺城下の石寺新市へ出した楽市の文書は、現存史料で確認できる最古の楽市令であり、織田信長の楽市楽座に先行する。一方、菅浦・今堀・大浦などには惣村の自治を伝える中世文書が大量に残り、用水や山林をめぐる村どうしの争論、村人自身が定めた掟（惣掟）の実態が読み取れる。日本の村落自治史の研究はこの近江の史料群に多くを負っている。比叡山延暦寺と園城寺、湖上交通を握った堅田の住人など、寺社と町の勢力も強かった。1571年、織田信長は延暦寺を攻撃している。',
    },
    year: [1467, 1600],
    keywords: ['近江', '琵琶湖', '六角氏', '浅井氏', '楽市', '惣村'],
    caveat:
      '1571年の延暦寺攻撃は、当時の記録では堂塔500余・僧俗3000人が失われたと伝えられますが、東塔・西塔・横川の発掘調査では大規模な焼失の痕跡が乏しく、被害は伝えられるほど広範ではなかった可能性が指摘されています。また「石寺の楽市が日本最初の楽市」という言い方は、現存史料で確認できる最古のものという意味であり、それ以前に例がなかったことを示すものではありません。',
    gameNote:
      '盤上の石高ランクと防御値は遊びやすさのために調整した相対値です。実際の近江は慶長3年の石高で約77.5万石でした。銭の産出は金山・銀山ではなく、琵琶湖水運と大津・草津の市がもたらした富を置き換えたものです。地形を寺社領（僧兵）としたのは延暦寺・園城寺の勢力を反映させたためで、実際の近江には湖岸の平地も広がります。令制では東山道の国ですが、盤上では京に隣接する畿内圏として扱っています。',
  },
};

/** 伊勢。湊（coast）の国。伊勢湾の海運と神宮の門前で成り立った。 */
export const ISE: Province = {
  id: 3,
  kind: 'province',
  name: '伊勢',
  reading: 'いせ',
  region: 'tokai',
  kokudaka: 2,
  defense: 2,
  terrain: 'coast',
  historicalKokudaka: 56.7,
  note: {
    summary:
      '伊勢神宮の門前と、伊勢湾の湊で成り立った国。参宮の人と海の物資が行き交い、南半を北畠氏が治めた。',
    detail: {
      1: 'いまの三重県の大部分にあたる国です。伊勢神宮があり、全国から人がおまいりに来ました。海に面していて、桑名や大湊などの港から船で荷物がはこばれます。米もよくとれる、ゆたかな国でした。',
      2: '現在の三重県の大半にあたる国。伊勢神宮を擁し、御師（おし）と呼ばれる人々が全国をまわって参宮をすすめたため、身分を問わず多くの人が伊勢を訪れた。伊勢湾に面し、桑名・大湊・安濃津などの湊が海運で栄え、安濃津は博多・坊津と並ぶ「三津」に数えられている。南半は伊勢国司の北畠氏、北半は関氏・神戸氏・長野氏などの小領主が分立した。1569年に織田信長が北畠氏を攻め、和睦の条件として次男の信雄を養子に入れ、伊勢の実権を握った。',
      3: '現在の三重県から伊賀・志摩を除いた地域。慶長3年(1598)の石高は約56.7万石で、伊勢平野の生産力に加え、伊勢湾の海運と伊勢神宮の門前経済が国を支えた。神宮の御師は全国に檀那場を持ち、暦や神札を配って参宮を組織したため、伊勢には中世を通じて各地から人と銭が集まった。参宮は武士に限られず、庶民も女性も加わっている。湊のうち安濃津は日本三津の一つに数えられたが、1498年の明応地震の津波で壊滅的な被害を受け、1522年に通りかかった連歌師の宗長は荒廃した姿を書き残している。政治的には南伊勢を伊勢国司北畠氏、北伊勢を関氏・神戸氏・長野氏らが分割し、統一権力が生まれにくい構造だった。1569年の大河内城攻めののち織田信雄が北畠家を継ぎ、伊勢は織田氏の勢力下に入る。長島（現在の桑名市の一部）には本願寺門徒の拠点があり、1570年から1574年にかけて織田氏との戦闘が続き、門徒や住民に多数の死者が出た。',
    },
    year: [1467, 1600],
    keywords: ['伊勢', '伊勢神宮', '御師', '北畠氏', '安濃津', '長島'],
    caveat:
      '安濃津の衰退を1498年の明応地震の津波によるものとする見方は有力ですが、湊の機能が失われた時期と原因については、海岸地形の変化や流通路の移動を含めて研究が続いています。「日本三津」という呼び方も後世の文献に多く見えるもので、当時の公式な区分ではありません。',
    gameNote:
      '盤上の石高ランクと防御値は遊びやすさのために調整した相対値です。実際の伊勢は慶長3年の石高で約56.7万石あり、盤上のランク2よりも実態は豊かでした。地形は湊（coast）としています。伊勢神宮を根拠に寺社領（僧兵）とする選び方もありますが、国全体の性格としては伊勢湾の海運の比重が大きいと判断しました。',
  },
};

/** 若狭。盤上で最も小さい国のひとつ。石高は低いが湊と街道の価値が高い。 */
export const WAKASA: Province = {
  id: 26,
  kind: 'province',
  name: '若狭',
  reading: 'わかさ',
  region: 'hokuriku',
  kokudaka: 1,
  defense: 1,
  terrain: 'coast',
  historicalKokudaka: 8.5,
  note: {
    summary:
      '日本海と京を最短で結ぶ小国。小浜の湊に集まった塩や海産物が街道で都へ運ばれ、古代には御食国と呼ばれた。',
    detail: {
      1: 'いまの福井県の南西部にあった国です。日本海に面していて、小浜という港がありました。とれた魚や塩は、山の道をこえて京都まではこばれました。国は小さいけれど、海と都をつなぐ大切な場所でした。',
      2: '現在の福井県南西部（嶺南）にあたる国。石高は約8.5万石と全国でも小さい部類だが、小浜湊が日本海側の物資を受け、そこから山越えの街道で京へ最短で運べたため、流通の面で重要だった。古代には塩や海産物を朝廷に納める「御食国（みけつくに）」の一つに数えられている。守護は室町時代に一色氏から武田氏（若狭武田氏）に移ったが、16世紀後半には家中が分裂し、隣国越前の朝倉氏の影響下に入った。1573年に朝倉氏が滅びたのち、織田信長は丹羽長秀を置いている。',
      3: '現在の福井県南西部。太閤検地の石高は約8.5万石で、これは1588年の浅野長吉（長政）による検地で確定した数値である。国土の大半が山地で、若狭湾のリアス海岸に小さな平地が点在するにすぎない。可耕地は乏しいが、小浜湊は日本海海運の要であり、北国から運ばれた米・塩・海産物や大陸からの物資がここで陸揚げされ、峠を越える街道で京へ運ばれた。京と日本海を結ぶ経路としては最短にあたる。古代には御食国として朝廷に贄（にえ）を貢納したとされ、その伝統が中世以降の食材輸送にもつながっている。1408年には南方から来た船が小浜に入港し、象などが献上されたという記録があり、湊の国際性を示す例としてよく挙げられる。守護は一色氏ののち15世紀半ばに若狭武田氏となったが、内訌によって統制を失い、1568年には越前の朝倉氏に事実上支配された。国が小さく守護の力も弱かったため、湊の富をめぐって国人・商人・寺社が競合する状況が長く続いている。',
    },
    year: [1467, 1600],
    keywords: ['若狭', '小浜', '御食国', '若狭武田氏', '日本海海運', '若狭街道'],
    caveat:
      '小浜から京へ至る道を「鯖街道」と呼ぶのは近年広まった呼び方で、当時の名称ではありません。1408年の南方からの船の来航と象の献上も後代の記録に見えるもので、船の出自など細部は確かめられていません。',
    gameNote:
      '盤上の石高ランク1と防御値1は遊びやすさのために調整した相対値です。実際の若狭は太閤検地で約8.5万石でした。ただし当時の若狭の重要さは米の量ではなく湊と街道にあり、盤上ではその性格を石高ではなく地形「湊」の側で表現しています。',
  },
};

/** 丹波。山国（弓）。京の西北に接し、山城が連なる。 */
export const TANBA: Province = {
  id: 27,
  kind: 'province',
  name: '丹波',
  reading: 'たんば',
  region: 'sanin',
  kokudaka: 2,
  defense: 3,
  terrain: 'mountain',
  historicalKokudaka: 26.4,
  note: {
    summary:
      '京の西北に接する山がちの国。盆地と峠が連なり、材木や産物を都へ送る一方、京へ入る軍事上の通路でもあった。',
    detail: {
      1: 'いまの京都府の中ほどと、兵庫県の東がわにあった国です。山が多く、あいだに小さな盆地がいくつもあります。京都のすぐとなりなので、木や作物を都へおくりました。山の上の城が多く、せめにくい国でした。',
      2: '現在の京都府中部と兵庫県東部にあたる国。丹波高地の山々のあいだに亀岡・園部・篠山・福知山などの盆地が点在する地形で、平地は少ない。京に隣接するため、材木や農産物を都へ供給する後背地であると同時に、京へ入る軍事上の通路でもあった。守護は細川氏だが、実際には守護代の内藤氏や、波多野氏・赤井（荻野）氏といった国人が各地の山城に拠って争っている。1575年から織田信長の命で明智光秀が攻略にあたったが、山城の抵抗は激しく、八上城・黒井城が落ちて平定が終わったのは1579年である。',
      3: '現在の京都府中部と兵庫県東部。慶長3年(1598)の石高は約26.4万石。丹波高地に亀岡・園部・篠山・福知山などの盆地が点在し、由良川と大堰川（保津川）が国内を刻む。平地は限られるが、栗や豆などの畑作と材木の産地として京の需要を支えた。大堰川の筏流しで運ばれた丹波の材木は、都の建築を長く支えている。政治的には管領細川氏が守護職を持ちながら、守護代内藤氏や波多野氏・赤井氏らの国人が山城に拠って分立し、まとまった支配が成立しにくかった。1575年、織田信長は明智光秀に丹波攻略を命じたが、1576年に波多野氏が離反して一度全面撤退している。再攻略で八上城が落ちたのは1579年6月、黒井城は同年8月で、着手から4年以上を要した。八上城は長期の兵糧攻めを受け、城内で多数の餓死者が出たと伝えられる。平定後、光秀は亀山城と福知山城を整備し、福知山では地子銭の免除や由良川の堤の普請を行ったと伝えられ、後年まで光秀を祀る動きが続いた。',
    },
    year: [1467, 1600],
    keywords: ['丹波', '丹波高地', '波多野氏', '明智光秀', '大堰川', '福知山'],
    caveat:
      '八上城の攻略について、明智光秀が母を人質に出したのに波多野氏の当主が処刑されたため母も殺された、という話が広く知られていますが、これは後世の書物に現れるもので同時代の史料では確かめられていません。「敵は本能寺にあり」の言葉も同時代の記録にはなく、後年の創作とみられています。福知山での地子免除や治水も伝承として語り継がれてきた部分が大きく、実施の範囲ははっきりしていません。',
    gameNote:
      '盤上の石高ランクと防御値は遊びやすさのために調整した相対値です。実際の丹波は慶長3年の石高で約26.4万石でした。地形は山国（弓）としています。丹波の山城が実際に攻めにくかったことは平定に4年以上かかった経緯からもうかがえますが、盤上の防御値3は他国との釣り合いで決めた数字で、史実の難しさを数値化したものではありません。',
  },
};
